import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Wallet,
  Users,
  Lightbulb,
  Image,
  LogOut,
  Clock,
  MessageCircle,
  ChevronRight,
  Plus
} from "lucide-react";
import { toast } from "sonner";

const TechnicianDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("boards");

  useEffect(() => {
    if (user?.role !== "technician" && user?.role !== "admin") {
      toast.error("Tech/Admin access only");
      navigate("/wallet");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [boardsRes, portfolioRes, tipsRes] = await Promise.all([
        axios.get(`${API}/technician/boards`, { withCredentials: true }),
        axios.get(`${API}/portfolio`, { withCredentials: true }),
        axios.get(`${API}/technician/tips`, { withCredentials: true })
      ]);
      setBoards(boardsRes.data);
      setPortfolio(portfolioRes.data);
      setTips(tipsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const groupedTips = tips.reduce((acc, tip) => {
    if (!acc[tip.category]) {
      acc[tip.category] = [];
    }
    acc[tip.category].push(tip);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex" data-testid="technician-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-black border-r border-gray-800 p-6 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <Wallet className="w-5 h-5 text-black" />
          </div>
          <div>
            <span className="font-display text-lg font-bold text-white">phygital</span>
            <p className="text-xs text-gray-600 font-mono">TECH</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-2 flex-1">
          <button
            onClick={() => setActiveTab("boards")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === "boards"
                ? "bg-white text-black"
                : "text-gray-500 hover:bg-gray-900 hover:text-white"
            }`}
          >
            <Users className="w-5 h-5" strokeWidth={1.5} />
            Appointments
          </button>
          <button
            onClick={() => setActiveTab("portfolio")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === "portfolio"
                ? "bg-white text-black"
                : "text-gray-500 hover:bg-gray-900 hover:text-white"
            }`}
          >
            <Image className="w-5 h-5" strokeWidth={1.5} />
            Portfolio
          </button>
          <button
            onClick={() => setActiveTab("tips")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === "tips"
                ? "bg-white text-black"
                : "text-gray-500 hover:bg-gray-900 hover:text-white"
            }`}
          >
            <Lightbulb className="w-5 h-5" strokeWidth={1.5} />
            Tips
          </button>
        </nav>

        {/* User and Logout */}
        <div className="pt-6 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-black font-medium">
              {user?.name?.charAt(0) || "T"}
            </div>
            <div>
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-gray-600 font-mono">Technician</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full text-gray-500 hover:text-white hover:bg-gray-900 justify-start"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto bg-gray-950">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-white mb-2">
            {activeTab === "boards" && "Active Appointments"}
            {activeTab === "portfolio" && "Portfolio Manager"}
            {activeTab === "tips" && "Salon Tips"}
          </h1>
          <p className="text-gray-500">
            {activeTab === "boards" && `${boards.length} appointments waiting`}
            {activeTab === "portfolio" && "Manage and tag your work"}
            {activeTab === "tips" && "Best practices and reminders"}
          </p>
        </div>

        {/* Appointments Tab */}
        {activeTab === "boards" && (
          <div className="grid gap-4">
            {boards.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No Active Appointments</h3>
                  <p className="text-gray-600">Customer boards will appear here when they check in and lock</p>
                </CardContent>
              </Card>
            ) : (
              boards.map((board) => (
                <Card
                  key={board.board_id}
                  className="bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors"
                  data-testid={`board-${board.board_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-white text-lg">
                            {board.customer_name || "Customer"}
                          </h3>
                          <Badge className={board.status === "in_service" ? "bg-green-600" : board.status === "locked" ? "bg-yellow-600" : "bg-gray-600"}>
                            {board.status}
                          </Badge>
                        </div>
                        {board.customer_phone && (
                          <p className="text-gray-500 text-sm font-mono mb-3">{board.customer_phone}</p>
                        )}
                        
                        {/* Preferences Summary */}
                        {board.preferences && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {board.preferences.shape && (
                              <Badge variant="outline" className="border-gray-700 text-gray-300">
                                {board.preferences.shape}
                              </Badge>
                            )}
                            {board.preferences.length && (
                              <Badge variant="outline" className="border-gray-700 text-gray-300">
                                {board.preferences.length}
                              </Badge>
                            )}
                            {board.preferences.finish && (
                              <Badge variant="outline" className="border-gray-700 text-gray-300">
                                {board.preferences.finish}
                              </Badge>
                            )}
                            {board.preferences.conversation_level && (
                              <Badge variant="outline" className="border-gray-700 text-gray-300">
                                {board.preferences.conversation_level}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Selections Summary */}
                        <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {board.selected_colors?.length || 0} colors, {board.selected_designs?.length || 0} designs
                          </span>
                        </div>

                        {/* Customer Notes */}
                        {board.customer_notes && (
                          <div className="mt-3 p-3 bg-gray-800 rounded-lg">
                            <p className="text-xs text-gray-600 uppercase mb-1">Customer Notes</p>
                            <p className="text-sm text-gray-300">{board.customer_notes}</p>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Chat
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Portfolio Tab */}
        {activeTab === "portfolio" && (
          <div>
            <div className="flex justify-end mb-6">
              <Button className="bg-white text-black hover:bg-gray-100 rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add to Portfolio
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolio.map((item) => (
                <Card key={item.portfolio_id} className="bg-gray-900 border-gray-800 overflow-hidden">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium text-white mb-1">{item.title}</h3>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {item.tags?.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="border-gray-700 text-gray-500 text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600">
                      {item.tagged_products?.length || 0} products tagged
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === "tips" && (
          <div className="space-y-6">
            {Object.entries(groupedTips).map(([category, categoryTips]) => (
              <div key={category}>
                <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {categoryTips.map((tip) => (
                    <Card key={tip.tip_id} className="bg-gray-900 border-gray-800">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white mb-1">{tip.title}</h4>
                            <p className="text-sm text-gray-500">{tip.content}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Back Link */}
        <div className="mt-8 pt-8 border-t border-gray-800">
          <Link
            to="/wallet"
            className="text-gray-500 hover:text-white flex items-center gap-2 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Wallet
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TechnicianDashboard;
